import React, { useState, useEffect } from "react";
import { Form, Space, Typography } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";

import FormItem from "./FormItem";

const { Text } = Typography;

/**
 * FormItemWithWarning - A wrapper around FormItem that can display warning messages
 * without blocking form submission
 *
 * @param {Function} warningValidator - Function that returns { shouldWarn: boolean, message: string }
 * @param {string} warningMessage - Custom warning message
 * @param {string} name - Field name for Form.useWatch
 * @param {React.ReactNode} children - Form input components
 * @param {Object} ...props - All other props passed to FormItem
 */
const FormItemWithWarning = ({ warningValidator, warningMessage, name, children, ...props }) => {
  const [warning, setWarning] = useState({ shouldWarn: false, message: "" });
  const form = Form.useFormInstance();
  const fieldValue = Form.useWatch(name, form);

  // This is to overcome the default margin from the FormItem component
  const overcomeDefaultMarginFromFormItem = -14;

  useEffect(() => {
    if (warningValidator && typeof warningValidator === "function") {
      try {
        const result = warningValidator(fieldValue, warningMessage);
        setWarning(result);
      } catch (error) {
        // Silently handle validator errors to prevent component crashes
        console.error("Warning validator error:", error);
        setWarning({ shouldWarn: false, message: "" });
      }
    }
  }, [fieldValue, warningValidator, warningMessage]);

  return (
    <>
      <FormItem name={name} {...props}>
        {children}
      </FormItem>
      {warning.shouldWarn && (
        <Space style={{ marginTop: overcomeDefaultMarginFromFormItem }}>
          <ExclamationCircleOutlined style={{ color: "orange" }} />
          <Text style={{ color: "orange", fontSize: "12px" }}>{warning.message}</Text>
        </Space>
      )}
    </>
  );
};

export default FormItemWithWarning;
